import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AddTenantRequest,
  AddTenantResponse,
  addTenantContract,
  CloneTenantRequest,
  CloneTenantResponse,
  cloneTenantContract,
  UpdateTenantRequest,
  UpdateTenantResponse,
  updateTenantContract,
  DeleteTenantResponse,
  deleteTenantContract
} from '@bliks-io/contracts';
import { apiClient } from '@/api/client';
import { tenantKeys } from '@/pages/admin/api/queries/useTenants';
import { cloneJobKeys } from '@/pages/admin/api/queries/useCloneJobs';
import { seatUsageKeys } from '@/api/query-keys';
import { invalidateEntity } from '@/api/cache-invalidation';

/**
 * Create tenant mutation
 *
 * Creates a new tenant in the system (super-admin only).
 * - Invalidates tenant list cache to refetch updated data
 *
 * @example
 * ```tsx
 * function CreateTenantForm() {
 *   const createTenant = useCreateTenant();
 *
 *   const handleSubmit = async (data: AddTenantRequest) => {
 *     try {
 *       const result = await createTenant.mutateAsync(data);
 *       console.log('Created tenant:', result.tenant);
 *       toast.success('Tenant created successfully');
 *     } catch (error) {
 *       toast.error('Failed to create tenant');
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       <Button loading={createTenant.isPending}>Create Tenant</Button>
 *     </form>
 *   );
 * }
 * ```
 */
export function useCreateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tenantData: AddTenantRequest): Promise<AddTenantResponse> => {
      const { data } = await apiClient.post<AddTenantResponse>(addTenantContract.path, tenantData);
      return data;
    },
    onSuccess: () => {
      // Invalidate tenant list to refetch with new tenant
      return queryClient.invalidateQueries({ queryKey: tenantKeys.lists() });
    }
  });
}

/**
 * Clone tenant mutation
 *
 * Starts cloning an existing tenant (super-admin only). Answers 202 with
 * `{ jobId, tenant }` once the tenant row and its job exist — the copy itself
 * takes ~65 s on a fat tenant and runs detached, so this resolving means
 * "accepted", not "finished". Everything judged synchronously (source exists,
 * slug free, body valid) still answers 4xx before any job is created.
 *
 * - Invalidates the tenant list, which already holds the new (inactive) row.
 * - Invalidates the clone jobs, which starts the poll that watches the copy.
 *
 * @example
 * ```tsx
 * const cloneTenant = useCloneTenant();
 * await cloneTenant.mutateAsync({ sourceId, data });
 * ```
 */
export function useCloneTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sourceId,
      data
    }: {
      sourceId: string;
      data: CloneTenantRequest;
    }): Promise<CloneTenantResponse> => {
      const path = cloneTenantContract.path.replace(':tenantId', sourceId);
      const { data: response } = await apiClient.post<CloneTenantResponse>(path, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.lists() });
      // The clone-jobs poll switches itself off while no job is open, so the
      // job this request just created has to wake it up.
      queryClient.invalidateQueries({ queryKey: cloneJobKeys.all });
    }
  });
}

/**
 * Update tenant mutation
 *
 * Updates an existing tenant by ID (super-admin only).
 * - Invalidates tenant list cache
 *
 * @example
 * ```tsx
 * function EditTenantForm({ tenantId }: { tenantId: string }) {
 *   const updateTenant = useUpdateTenant();
 *
 *   const handleSubmit = async (data: UpdateTenantRequest) => {
 *     try {
 *       await updateTenant.mutateAsync({ id: tenantId, data });
 *       toast.success('Tenant updated successfully');
 *     } catch (error) {
 *       toast.error('Failed to update tenant');
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       <Button loading={updateTenant.isPending}>Update Tenant</Button>
 *     </form>
 *   );
 * }
 * ```
 */
export function useUpdateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data
    }: {
      id: string;
      data: UpdateTenantRequest;
    }): Promise<UpdateTenantResponse> => {
      const path = updateTenantContract.path.replace(':tenantId', id);
      const { data: response } = await apiClient.patch<UpdateTenantResponse>(path, data);
      return response;
    },
    onSuccess: (_response, { id, data }) => {
      // Invalidate tenant list to refetch with updated data
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: tenantKeys.lists() }),
        // Only a patch that actually moved a cap changes what the usage read reports.
        data.seatLimits && queryClient.invalidateQueries({ queryKey: seatUsageKeys.tenant(id) })
      ]);
    }
  });
}

/**
 * Delete tenant mutation
 *
 * Deletes a tenant by ID (super-admin only).
 * - Invalidates tenant list cache
 *
 * @example
 * ```tsx
 * function DeleteTenantButton({ tenantId }: { tenantId: string }) {
 *   const deleteTenant = useDeleteTenant();
 *
 *   const handleDelete = async () => {
 *     if (!confirm('Are you sure?')) return;
 *
 *     try {
 *       await deleteTenant.mutateAsync(tenantId);
 *       toast.success('Tenant deleted successfully');
 *     } catch (error) {
 *       toast.error('Failed to delete tenant');
 *     }
 *   };
 *
 *   return (
 *     <Button
 *       variant="destructive"
 *       loading={deleteTenant.isPending}
 *       onClick={handleDelete}
 *     >
 *       Delete
 *     </Button>
 *   );
 * }
 * ```
 */
export function useDeleteTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tenantId: string): Promise<DeleteTenantResponse> => {
      const path = deleteTenantContract.path.replace(':tenantId', tenantId);
      const { data } = await apiClient.delete<DeleteTenantResponse>(path);
      return data;
    },
    onSuccess: () => {
      // Deleting a tenant flips homeOrgDeleted for users pinned to it, who can
      // be listed under EVERY other tenant and in org-member lists — so
      // invalidate the whole tenant family (list + all per-tenant user lists)
      // and the org-member entity family, not just this tenant's keys.
      // Only the tenant list is awaited: the org-member family fans out to
      // app-wide reads the confirm dialog has no reason to wait for.
      void invalidateEntity(queryClient, 'orgMember');
      return queryClient.invalidateQueries({ queryKey: tenantKeys.all });
    }
  });
}
